import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import {
  formatNumberWithConstraints,
  formatUSPhoneNumber,
} from "src/services/format";
import * as Answer from "src/services/formTemplate/answer";
import * as AF from "src/types/formTemplate";

export enum AnswerType {
  String = "string",
  File = "file",
  Custom = "custom",
}

export type StringAnswer = {
  type: AnswerType.String;
  question: string;
  answer: string;
};

export type FileUploadAnswer = {
  type: AnswerType.File;
  question: string;
  documentID: string;
  url: string;
};

export type CustomQuestionAnswers = {
  type: AnswerType.Custom;
  question: string;
  values: [string, string][];
};

export type ParsedAnswer =
  | StringAnswer
  | FileUploadAnswer
  | CustomQuestionAnswers;

export const parseAnswer = (
  question: AF.Question<AF.WithId>,
  answer: Answer.FormikFieldValue,
  timezoneName: string
): ParsedAnswer | undefined => {
  if (!answer) return undefined;
  switch (question.type) {
    case "FreeText":
    case "Email":
      return {
        type: AnswerType.String,
        question: question.question,
        answer: answer.toString(),
      };
    case "Date": {
      const stringValue = answer.toString();
      return {
        type: AnswerType.String,
        question: question.question,
        answer: isValid(parseISO(stringValue))
          ? formatInTimeZone(
              stringValue,
              timezoneName,
              question.constraints?.format || DEFAULT_DATE_TYPE_FORMAT
            )
          : stringValue,
      };
    }
    case "Number":
      return {
        type: AnswerType.String,
        question: question.question,
        answer: formatNumberWithConstraints(answer, question.constraints),
      };
    case "PhoneNumber":
      return {
        type: AnswerType.String,
        question: question.question,
        answer: formatUSPhoneNumber(answer.toString()),
      };
    case "Grades":
    case "SingleSelect": {
      if (Answer.isSingleSelectAnswer(answer)) {
        const answerID: string = answer;
        const option = question.options.find((x) => x.id === answerID);
        if (option)
          return {
            type: AnswerType.String,
            question: question.question,
            answer: option.isArchived
              ? `${option.label} (archived)`
              : option.label,
          };
      }
      return undefined;
    }
    case "MultiSelect": {
      if (Answer.isMultiSelectAnswer(answer)) {
        const answerIDs: string[] = answer;
        const value: string = answerIDs
          .map((answerID) => {
            const option = question.options.find((x) => x.id === answerID);
            if (option)
              return option.isArchived
                ? `${option.label} (archived)`
                : option.label;
            else return "";
          })
          .filter((x) => x)
          .join("\n");
        return {
          type: AnswerType.String,
          question: question.question,
          answer: value,
        };
      }
      return undefined;
    }
    case "FileUpload": {
      return undefined; // for now, dont include attachments on PDF. note, attachments may be PDF or Images. A FileUpload may have 1 or more attachments
    }

    case "Address": {
      if (Answer.isAddressAnswer(answer)) {
        if (
          !answer.street_address ||
          !answer.city ||
          !answer.state ||
          !answer.zip_code
        )
          return undefined;
        const value: string =
          answer.street_address +
          "\n" +
          (answer.street_address_line_2
            ? answer.street_address_line_2 + "\n"
            : "") +
          answer.city +
          " " +
          answer.state +
          ", " +
          answer.zip_code;
        return {
          type: AnswerType.String,
          question: question.question,
          answer: value,
        };
      } else return undefined;
    }
    case "CustomQuestion":
      if (Answer.isCustomQuestionAnswer(answer)) {
        const values: [string, string][] = Object.entries(
          answer.answersByQuestionId
        ).map(([qustionId, answer]) => {
          const subQuestion = question.nestedQuestions.find(
            (x) => x.id === qustionId
          );
          if (!subQuestion) return ["", ""];
          const parsedAnswer = parseAnswer(subQuestion, answer, timezoneName);
          if (!parsedAnswer || parsedAnswer?.type !== AnswerType.String)
            return ["", ""];
          return [subQuestion.question, parsedAnswer.answer];
        });
        const filteredValues: [string, string][] = values.filter(
          ([_q, a]) => a
        );
        return {
          type: AnswerType.Custom,
          question: question.question,
          values: filteredValues,
        };
      } else return undefined;
    default: {
      const _exhaustiveCheck: never = question;
      return _exhaustiveCheck;
    }
  }
};

export type SectionData = {
  sectionName: string;
  questionAnswers: ParsedAnswer[];
};

export type HeaderData = {
  fullName: string;
  dateOfBirth: string;
  organizationName: string;
  enrollmentPeriodName: string;
  formName: string;
};

export type Data = {
  header: HeaderData;
  sections: SectionData[];
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 22,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 20,
    backgroundColor: "#ffffff",
  },
  headerContainer: {
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 20,
  },
  headerColumn: {
    flexDirection: "column",
    gap: 4,
  },
  headerText: {
    color: "#2D3748",
    fontSize: 16,
  },
  headerTitle: {
    color: "#2D3748",
    fontSize: 16,
    fontWeight: 600,
  },
  section: {
    breakInside: "avoid",
  },
  sectionTitle: {
    color: "#2D3748",
    fontSize: 16,
    fontWeight: 600,
    paddingTop: 12,
    paddingBottom: 12,
    marginBottom: 16,
  },
  questionBlock: {
    marginBottom: 16,
  },
  question: {
    color: "#4A5568",
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 4,
  },
  answer: {
    color: "#1A202C",
    fontSize: 16,
    fontWeight: 400,
  },
});

const Header: React.FC<{ data: HeaderData }> = ({ data }) => (
  <View style={styles.headerContainer}>
    <View style={styles.headerContent}>
      <View style={styles.headerColumn}>
        <Text style={styles.headerTitle}>{data.organizationName}</Text>
        <Text style={styles.headerText}>{data.enrollmentPeriodName}</Text>
        <Text style={styles.headerTitle}>{data.formName}</Text>
      </View>
      <View style={styles.headerColumn}>
        <Text style={styles.headerTitle}>Name: {data.fullName}</Text>
        <Text style={styles.headerText}>DOB: {data.dateOfBirth}</Text>
      </View>
    </View>
  </View>
);

export const FormPdf: React.FC<{ data: Data }> = ({ data }) => {
  return (
    <Document>
      {data.sections
        .filter((section) => section.questionAnswers.length > 0)
        .map((section, sectionIndex) => (
          <Page key={`pdf-section-${sectionIndex}`} style={styles.page}>
            <Header key={`pdf-header-${sectionIndex}`} data={data.header} />
            <View
              key={`pdf-section-view-${sectionIndex}`}
              style={styles.section}
            >
              <Text style={styles.sectionTitle}>{section.sectionName}</Text>
              {section.questionAnswers.map((qa, qaIndex) => (
                <View
                  key={`pdf-question-${sectionIndex}-${qaIndex}`}
                  style={styles.questionBlock}
                >
                  <Text style={styles.question}>{qa.question}</Text>
                  {qa.type === AnswerType.String && !!qa.answer && (
                    <Text style={styles.answer}>{qa.answer}</Text>
                  )}
                  {qa.type === AnswerType.Custom &&
                    qa.values.length > 0 &&
                    qa.values.map((cqa, cqaIdx) => (
                      <Text
                        key={`pdf-cqa-${sectionIndex}-${qaIndex}-${cqaIdx}`}
                        style={styles.answer}
                      >{`${cqa[0]}: ${cqa[1]}`}</Text>
                    ))}
                  {qa.type === AnswerType.File && (
                    <View>
                      <Image
                        key={`pdf-image-${sectionIndex}-${qaIndex}`}
                        src={qa.url}
                        style={{
                          width: "500",
                        }}
                      />
                    </View>
                  )}
                </View>
              ))}
            </View>
          </Page>
        ))}
    </Document>
  );
};
